import { Show } from '@clerk/react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect } from 'react';

import { debugAuth } from '@/auth/debug';
import { sanitizeReturnTo } from '@/auth/roleDecision';
import { useCurrentRole } from '@/auth/useCurrentRole';

/**
 * Post-sign-in dispatch. Routes the signed-in user to the surface that
 * matches their derived role — same `useCurrentRole` source of truth
 * `RoleGuard` uses, so dispatch and per-route gating can't disagree:
 *
 *   - `isCustomer` (Clerk session has an active org) → /portal
 *   - `isPractitioner` (@inputrecovery.com email + no org)         → /accounts
 *   - else (role still resolving, e.g. a freshly-invited customer
 *     whose org membership hasn't loaded yet) → stay on the
 *     "Finishing sign-in…" screen until Clerk hydrates.
 *
 * **Why customer wins**: matches `useCurrentRole`'s precedence (which
 * matches the backend's `_provision_clerk_user` order). A practitioner
 * who sends themselves a customer invite (e.g. david@inputrecovery.com
 * inviting david@inputrecovery.com to test the portal) becomes a
 * customer of that account; routing them to /accounts would trip the
 * backend's `require_practitioner` guard and 403-storm the console.
 *
 * Previous shape routed by email domain alone — broke the day David
 * sent himself a customer invite (2026-06-08): the customer org
 * membership made the backend treat him as a customer while the
 * frontend kept routing him to /accounts.
 */
export function SignInDonePage() {
  return (
    <>
      <Show when="signed-in">
        <Dispatcher />
      </Show>
      <Show when="signed-out">
        <BackToSignIn />
      </Show>
    </>
  );
}

function Dispatcher() {
  const role = useCurrentRole();
  const navigate = useNavigate();
  // Deep link carried through the sign-in round trip (RoleGuard →
  // /sign-in → Clerk → here). Honored before the role-home dispatch;
  // if it points at a wrong-role page, that page's own RoleGuard
  // re-bounces the user home, so no role check is needed here.
  const { redirect } = useSearch({ strict: false }) as { redirect?: string };
  const returnTo = sanitizeReturnTo(redirect ?? null);

  useEffect(() => {
    if (!role.isAuthReady || !role.isSignedIn) return;
    if (debugAuth()) {
      // eslint-disable-next-line no-console
      console.log('[auth.SignInDone] dispatching', {
        isPractitioner: role.isPractitioner,
        isCustomer: role.isCustomer,
        email: role.email,
        orgId: role.orgId,
        returnTo,
      });
    }
    if (returnTo) {
      // Full-location replace rather than SPA navigate: the target is an
      // arbitrary sanitized path string, outside the typed route map.
      window.location.replace(returnTo);
    } else if (role.isCustomer) {
      void navigate({ to: '/portal' });
    } else if (role.isPractitioner) {
      void navigate({ to: '/customers' });
    } else {
      // No recognized role — neither @inputrecovery.com email nor an
      // active org membership. Two ways this happens:
      //  (a) freshly-invited customer whose orgId hasn't hydrated yet
      //      — Clerk re-fires this effect once the membership lands,
      //      and the `isCustomer` branch above takes over.
      //  (b) genuinely unbound user (Clerk session exists but no
      //      account binding) — could be a customer mid-provisioning
      //      or someone who signed up outside an invite flow.
      // Either way, defaulting to /portal is the safe landing:
      // PortalSummaryCard's signed-state error path catches the 403
      // and renders "Your account is being set up — ask your
      // InputRecovery practitioner to verify the invite for <email>",
      // which is the right message for both cases. Going to /accounts
      // (the prior path on email-domain miss) would 403 the
      // practitioner-only API instead.
      void navigate({ to: '/portal' });
    }
  }, [role, navigate, returnTo]);

  return <p className="px-6 py-10 text-sm text-brand-slate">Finishing sign-in…</p>;
}

function BackToSignIn() {
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Keep the query string so a pending ?redirect= deep link survives
      // the bounce back to sign-in.
      window.location.replace('/sign-in' + window.location.search);
    }
  }, []);
  return null;
}
